// HTTP client + session storage + auto-refresh bus.
// Ported from the monolithic frontend with constants moved into
// `shared/config` and tighter typing on the refresh payload. API
// contracts (URL, request/response shapes) are intentionally identical.

import { SESSION_STORAGE_KEY, withApiBase } from '@/shared/config/constants';

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  userId?: number | null;
  userName: string;
  userEmail: string;
  avatarColor?: string | null;
  avatarIcon?: string | null;
  avatarUrl?: string | null;
  demoUser?: boolean;
};

type FetchOptions = RequestInit & {
  skipAuth?: boolean;
  skipRefresh?: boolean;
};

type ErrorPayload = {
  message?: string;
  error?: string;
};

const isBrowser = typeof window !== 'undefined';

const LEGACY_KEYS = [
  'kaban.accessToken',
  'kaban.refreshToken',
  'kaban.userId',
  'kaban.userName',
  'kaban.userEmail',
  'kaban.userAvatarColor',
  'kaban.userAvatarIcon',
  'kaban.userAvatarUrl',
  'kaban.demoUser',
] as const;

// Migrate old per-key storage to single-key JSON (one-time, on first read).
const migrateOldKeys = () => {
  if (!isBrowser) return;
  const oldToken = window.localStorage.getItem('kaban.accessToken');
  if (!oldToken) return;
  const session: AuthSession = {
    accessToken: oldToken,
    refreshToken: window.localStorage.getItem('kaban.refreshToken') ?? '',
    userId: (() => {
      const v = window.localStorage.getItem('kaban.userId');
      return v ? Number(v) : null;
    })(),
    userName: window.localStorage.getItem('kaban.userName') ?? '',
    userEmail: window.localStorage.getItem('kaban.userEmail') ?? '',
    avatarColor: window.localStorage.getItem('kaban.userAvatarColor'),
    avatarIcon: window.localStorage.getItem('kaban.userAvatarIcon'),
    avatarUrl: window.localStorage.getItem('kaban.userAvatarUrl'),
    demoUser: window.localStorage.getItem('kaban.demoUser') === 'true',
  };
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  LEGACY_KEYS.forEach((k) => window.localStorage.removeItem(k));
};
migrateOldKeys();

export const getAuthSession = (): AuthSession | null => {
  if (!isBrowser) return null;
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as AuthSession;
    if (!session.accessToken || !session.refreshToken || !session.userName || !session.userEmail) return null;
    return session;
  } catch {
    return null;
  }
};

export const setAuthSession = (session: AuthSession) => {
  const prev = isBrowser ? window.localStorage.getItem(SESSION_STORAGE_KEY) : null;
  if (isBrowser) window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  // Only notify when access token actually changed (not profile-only updates).
  let prevToken: string | null = null;
  if (prev) {
    try {
      prevToken = (JSON.parse(prev) as AuthSession).accessToken;
    } catch {
      /* noop */
    }
  }
  if (prevToken !== session.accessToken) {
    tokenChangeListeners.forEach((fn) => {
      try {
        fn();
      } catch {
        /* noop */
      }
    });
  }
};

export const clearAuthSession = () => {
  if (isBrowser) window.localStorage.removeItem(SESSION_STORAGE_KEY);
};

export const isAuthenticated = () => Boolean(getAuthSession()?.accessToken);
export const getAccessToken = () => getAuthSession()?.accessToken ?? null;
export const isDemoUser = () => getAuthSession()?.demoUser === true;
export const getRefreshToken = () => getAuthSession()?.refreshToken ?? null;

/* ─── token change event bus ───
   RealtimeClient listens for token changes to reconnect SSE with fresh token. */

type TokenChangeListener = () => void;
const tokenChangeListeners: TokenChangeListener[] = [];

export const onTokenChange = (listener: TokenChangeListener) => {
  tokenChangeListeners.push(listener);
  return () => {
    const idx = tokenChangeListeners.indexOf(listener);
    if (idx >= 0) tokenChangeListeners.splice(idx, 1);
  };
};

export type SessionProfileLike = {
  id?: number | null;
  name: string;
  email: string;
  avatarColor?: string | null;
  avatarIcon?: string | null;
  avatarUrl?: string | null;
};

export const syncSessionProfile = (profile: SessionProfileLike) => {
  const current = getAuthSession();
  if (!current) return;
  setAuthSession({
    ...current,
    userId: profile.id ?? current.userId ?? null,
    userName: profile.name,
    userEmail: profile.email,
    avatarColor: profile.avatarColor ?? null,
    avatarIcon: profile.avatarIcon ?? null,
    avatarUrl: profile.avatarUrl ?? null,
  });
};

/* ─── auth event bus ───
   Components can listen for 'auth:logout' to redirect or update UI
   when the session becomes invalid.                                  */

type AuthEventListener = () => void;
const authLogoutListeners: AuthEventListener[] = [];

export const onAuthLogout = (listener: AuthEventListener) => {
  authLogoutListeners.push(listener);
  return () => {
    const idx = authLogoutListeners.indexOf(listener);
    if (idx >= 0) authLogoutListeners.splice(idx, 1);
  };
};

/* ─── logout cleanup registry ───
   Consumers (QueryClient, RealtimeClient, etc.) register cleanup handlers
   here so fireAuthLogout can flush all caches/connections without creating
   circular imports. */

type CleanupHandler = () => void;
const logoutCleanupHandlers: CleanupHandler[] = [];

export const registerLogoutCleanup = (fn: CleanupHandler) => {
  logoutCleanupHandlers.push(fn);
};

const fireAuthLogout = () => {
  clearAuthSession();
  logoutCleanupHandlers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* noop */
    }
  });
  authLogoutListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* noop */
    }
  });
};

/** Login path respecting Vite `base` (not always "/"). */
const getLoginPath = (): string => {
  const raw = (import.meta.env?.BASE_URL as string | undefined) ?? '/';
  const trimmed = raw.replace(/\/+$/, '');
  return trimmed ? `${trimmed}/login` : '/login';
};

/** Session invalid: clear storage, notify UI, hard-redirect (more reliable than React Navigate). */
const hardRedirectToLogin = () => {
  inflightRefresh = null;
  fireAuthLogout();
  if (isBrowser) {
    window.location.replace(getLoginPath());
  }
};

/** Full logout: cancels refresh, clears storage, notifies subscribers, hard-navigates to /login. */
export const performLogout = () => {
  inflightRefresh = null;
  fireAuthLogout();
  if (isBrowser) {
    window.location.assign(getLoginPath());
  }
};

/* ─── helpers ─── */

const buildHeaders = (init?: HeadersInit, options?: FetchOptions) => {
  const headers = new Headers(init ?? {});
  if (!headers.has('Content-Type') && options?.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!options?.skipAuth) {
    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
};

const extractErrorMessage = async (response: Response) => {
  try {
    const payload = (await response.json()) as ErrorPayload;
    return payload.message || payload.error || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
};

/* ─── token refresh with request coalescing ───
   If multiple 401s happen simultaneously, only ONE refresh call
   is made. All waiting callers share the same promise.             */

let inflightRefresh: Promise<boolean> | null = null;

export const refreshAccessToken = async (): Promise<boolean> => {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    const currentSession = getAuthSession();
    const refreshToken = currentSession?.refreshToken;
    if (!refreshToken) {
      fireAuthLogout();
      return false;
    }

    try {
      const response = await fetch(withApiBase('/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        fireAuthLogout();
        return false;
      }

      const payload = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
        id?: number | null;
        name: string;
        email: string;
        avatarColor?: string | null;
        avatarIcon?: string | null;
        avatarUrl?: string | null;
        demoUser?: boolean;
      };

      // Validate that refreshed token belongs to the same user.
      if (currentSession.userId != null && payload.id != null && currentSession.userId !== payload.id) {
        fireAuthLogout();
        return false;
      }

      setAuthSession({
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        userId: payload.id ?? null,
        userName: payload.name,
        userEmail: payload.email,
        avatarColor: payload.avatarColor ?? null,
        avatarIcon: payload.avatarIcon ?? null,
        avatarUrl: payload.avatarUrl ?? null,
        demoUser: payload.demoUser ?? false,
      });

      return true;
    } catch {
      // Network down — keep session, no redirect.
      return false;
    }
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
};

/* ─── main fetch wrapper ─── */

export async function apiFetch<T = unknown>(path: string, options: FetchOptions = {}): Promise<T> {
  const response = await fetch(withApiBase(path), {
    cache: 'no-store',
    ...options,
    headers: buildHeaders(options.headers, options),
  });

  if (response.status === 403) {
    const msg = await extractErrorMessage(response);
    throw new Error(msg || 'Нет доступа');
  }

  if (response.status === 401 && !options.skipRefresh && !options.skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return apiFetch<T>(path, { ...options, skipRefresh: true });
    }
    hardRedirectToLogin();
    throw new Error('Сессия истекла');
  }

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response));
  }

  return parseResponse<T>(response);
}

/* ─── authenticated file download ───
   A bare <a href="/api/..."> navigation carries no Authorization header, so any
   attachment endpoint behind auth answers 401. This fetches the file WITH the
   Bearer token, then saves the resulting blob under `filename`. Endpoint-agnostic:
   works for the static `/uploads/...` path and for an authenticated
   `/api/cards/{id}/attachments/{id}/download`. Refreshes the token once on 401. */

export async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const doFetch = () => {
    const headers = new Headers();
    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(path, { cache: 'no-store', headers });
  };

  let response = await doFetch();
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      hardRedirectToLogin();
      throw new Error('Сессия истекла');
    }
    response = await doFetch();
  }
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response));
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after a delay so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
}
